import { useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateConnection,
  useEditConnection,
} from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { MySQLEnvironmentConfig } from "src/connections/config/database/mysql/MySQLEnvironmentConfig";
import { DatabasePermissionsPill } from "src/connections/config/database/shared/DatabasePermissionsPill";
import { EnableNonProdConfigsField } from "src/connections/config/EnableNonProdConfigsField";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { EnvironmentTabs } from "src/connections/config/shared/EnvironmentTabs";
import { errorProps } from "src/connections/config/shared/errorProps";
import {
  convertBEConnectionToMySQLConnectionConfigInputs,
  convertMySQLConnectionConfigInputsToBEConnection,
  getMySQLConnectionConfigInputsDefaultValues,
} from "src/connections/model/mysql";
import { MySQLConnectionConfigInputs } from "src/connections/types";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  onClose: () => void;
  workspaceUrl: string;
  connection?: Connection;
};

export const MySQLConnectionConfigForm: React.FC<Props> = ({
  onClose,
  workspaceUrl,
  connection,
}) => {
  const defaultValues = useMemo(() => {
    if (connection) {
      return convertBEConnectionToMySQLConnectionConfigInputs(connection);
    } else {
      return getMySQLConnectionConfigInputsDefaultValues();
    }
  }, [connection]);

  const formMethods = useForm<MySQLConnectionConfigInputs>({
    defaultValues,
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = formMethods;

  const enableNonProdConfigs = watch("enableNonProdConfigs");

  const createConnectionMutation = useCreateConnection(workspaceUrl);
  const editConnectionMutation = useEditConnection(
    workspaceUrl,
    connection?.id,
  );

  const onSubmit = async (data: MySQLConnectionConfigInputs) => {
    const payload = convertMySQLConnectionConfigInputsToBEConnection(data);
    const action = connection ? "updated" : "created";
    try {
      if (connection) {
        await editConnectionMutation.mutateAsync({
          id: connection.id,
          payload,
        });
      } else {
        await createConnectionMutation.mutateAsync(payload);
      }
      toastActions.success({
        title: `Connection ${action}`,
        description: `Connection ${data.name} has been ${action}.`,
      });
      onClose();
    } catch {
      toastActions.failure({
        title: "Error",
        description: `The connection could not be ${action}`,
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <FormItem
            description="A name that describes the new connection"
            gap="sm"
            label="Connection name"
            isRequired
          >
            {Boolean(errors.name) && (
              <ErrorHint>{errors.name?.message}</ErrorHint>
            )}
            <Input
              data-loc="mysql-connection-name"
              placeholder="e.g. MySQL"
              fullWidth
              {...errorProps(isFieldErrored(errors, "name"))}
              {...register("name", {
                required: "Connection name is required",
                validate: validateNotStartsWithInternalPrefix(
                  `Connection name cannot start with ${taktileInternalPrefix}`,
                ),
              })}
            />
          </FormItem>
          <EnableNonProdConfigsField<MySQLConnectionConfigInputs>
            dataLoc="mysql-connection-env-aware-switch"
            subtitle="You can set different credentials for test runs and sandbox DB queries"
            title="Use separate credentials for non-production DB queries"
          />
          {enableNonProdConfigs ? (
            <EnvironmentTabs
              prodContent={
                <MySQLEnvironmentConfig
                  environment="production"
                  labelsPrefix="Production"
                />
              }
              prodError={Boolean(errors.productionConfig)}
              sandboxContent={
                <MySQLEnvironmentConfig
                  environment="sandbox"
                  labelsPrefix="Sandbox"
                />
              }
              sandboxError={Boolean(errors.sandboxConfig)}
            />
          ) : (
            <MySQLEnvironmentConfig environment="production" labelsPrefix="" />
          )}
          <DatabasePermissionsPill />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="mysql-connection-save"
              isDisabled={isSubmitting}
              htmlType="submit"
              isLoading={isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        ></Modal.Footer>
      </form>
    </FormProvider>
  );
};
